"use client";

import { useEffect, useRef, useState } from "react";
import HistoryTimelineItem from "./history-timeline-item";
import { HISTORY_ITEMS } from "../constants/history-data";

/**
 * Timeline component with scroll-based progress animation
 * The blue timeline line fills based on scroll position
 */
export default function HistoryTimeline() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;

      const timeline = timelineRef.current;
      const timelineRect = timeline.getBoundingClientRect();
      const timelineTop = timelineRect.top + window.scrollY;
      const timelineHeight = timelineRect.height;
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;

      // Calculate progress based on viewport center (same as marker calculation)
      const viewportCenter = scrollY + viewportHeight * 0.55;
      const scrollProgress = (viewportCenter - timelineTop) / timelineHeight;
      const clampedProgress = Math.max(0, Math.min(1, scrollProgress));
      setProgress(clampedProgress);

      // Determine active item based on scroll position
      const items = itemRefs.current.filter(Boolean);
      if (items.length === 0) return;

      let newActiveIndex = 0;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item) continue;

        const itemRect = item.getBoundingClientRect();
        const itemTop = itemRect.top + window.scrollY;
        const itemCenter = itemTop + itemRect.height / 2;

        // Activate when item reaches 40% of viewport height (60/40 split)
        if (scrollY + viewportHeight * 0.55 >= itemCenter) {
          newActiveIndex = i;
        }
      }

      setActiveIndex(newActiveIndex);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const setItemRef = (index: number) => (node: HTMLDivElement | null) => {
    itemRefs.current[index] = node;
  };

  return (
    <div className="relative w-full py-8 md:py-12">
      {/* Timeline container - only contains items, not the "Stand heute" text */}
      <div ref={timelineRef} className="relative">
        {/* Timeline line - only spans the items container */}
        <div className="absolute top-0 left-4 h-full w-1 bg-gray-300 md:left-1/2 md:-translate-x-1/2">
          {/* Progress fill */}
          <div
            className="absolute top-0 h-full w-full origin-top bg-[#1B4C85] transition-all duration-300 ease-out"
            style={{
              transform: `scaleY(${progress})`,
            }}
          />
        </div>

        {/* Timeline items */}
        <div className="relative space-y-36 pt-12 md:space-y-52">
          {HISTORY_ITEMS.map((item, index) => (
            <HistoryTimelineItem
              key={item.year}
              item={item}
              index={index}
              isActive={index <= activeIndex}
              itemRef={setItemRef(index)}
            />
          ))}
        </div>
      </div>

      {/* Stand heute title - centered below timeline, outside the timeline container */}
      <div className="relative mt-12 flex w-full justify-center md:mt-16">
        <h2 className="text-foreground text-center text-xl font-bold sm:text-2xl md:text-3xl lg:text-4xl">
          Stand heute
        </h2>
      </div>
    </div>
  );
}
